import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { profileApi } from '@teachur/http-client';

export const useStripeCard = () => {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();

  const { mutate: addCard, isPending } = useMutation({
    mutationFn: async () => {
      if (!stripe || !elements) {
        throw new Error('Stripe is not initialized.');
      }

      const cardElement = elements.getElement('card');
      if (!cardElement) {
        throw new Error('Card element not found.');
      }

      const { error, token } = await stripe.createToken(cardElement);

      if (error) {
        throw new Error(error.message);
      }

      if (!token) {
        throw new Error('Failed to create card token.');
      }

      const response = await profileApi.updateCard(token.id);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Card added successfully.');
      queryClient.invalidateQueries({ queryKey: ['billingInfo'] });
      elements?.getElement('card')?.clear();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add card.');
    },
  });

  const isReady = !!stripe && !!elements;

  return { addCard, isPending, isReady };
};
